const VALID_CATEGORIES = ['ethnic', 'lifestyle'];
const DEFAULT_CATEGORY = 'ethnic';
const SAFE_HOME_QUERY_PARAMS = [
  '_fd',
  'pb',
  'preview_theme_id',
  'theme_editor_session_token',
];

class CategorySwitcher {
  constructor() {
    this.currentCategory = null;
    this.isSwitching = false;
    this.homeLinkDelegationBound = false;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
    } else {
      this.init();
    }
  }

  init() {
    const urlParams = new URLSearchParams(window.location.search);
    const viewCategory = this._normalizeCategory(urlParams.get('view'));
    const menuCategory = this._normalizeCategory(urlParams.get('menu_category'));
    const savedCategory = viewCategory || menuCategory || this._getStoredCategory() || DEFAULT_CATEGORY;

    if (viewCategory) {
      this._setStoredCategory(viewCategory);
    } else if (menuCategory) {
      this._setStoredCategory(menuCategory);
    }

    this._syncHomeLinks(savedCategory);
    this._bindHomeLinkDelegation();

    if (this._isHomePath(window.location.pathname) && !viewCategory) {
      window.location.replace(this._buildViewUrl(savedCategory));
      return;
    }

    this.switchCategory(savedCategory, false);

    document.addEventListener('click', (event) => {
      const categoryBtn = event.target.closest(
        '.menu-btn, .fullscreen-category-nav__item, .drawer-menu-category-btn'
      );

      if (!categoryBtn || !categoryBtn.dataset.category) return;

      event.preventDefault();
      event.stopPropagation();
      this.switchCategory(categoryBtn.dataset.category, true);
    });
  }

  async switchCategory(category, animate = true) {
    const normalizedCategory = this._normalizeCategory(category);
    if (!normalizedCategory) return;

    if (normalizedCategory === this.currentCategory || this.isSwitching) return;

    this.isSwitching = true;

    const previousCategory = this.currentCategory;
    this.currentCategory = normalizedCategory;

    this._setStoredCategory(normalizedCategory);
    this._syncHomeLinks(normalizedCategory);

    if (animate) {
      const currentView = this._normalizeCategory(
        new URLSearchParams(window.location.search).get('view')
      );
      const targetUrl = this._buildViewUrl(normalizedCategory);

      if (!this._isHomePath(window.location.pathname) || currentView !== normalizedCategory) {
        sessionStorage.setItem('categorySwitchTarget', normalizedCategory);
        window.location.replace(targetUrl);
        return;
      }
    }

    this._applyCategory(normalizedCategory);
    this.updateButtonStates(normalizedCategory);
    this.dispatchCategoryChange(normalizedCategory, previousCategory);

    if (animate) {
      setTimeout(() => {
        this.isSwitching = false;
      }, 150);
    } else {
      this.isSwitching = false;
    }
  }

  _applyCategory(category) {
    document.body.classList.remove('show-ethnic', 'show-lifestyle');
    document.body.classList.add('show-' + category);
    document.body.setAttribute('data-category', category);
  }

  _normalizeCategory(category) {
    return VALID_CATEGORIES.includes(category) ? category : null;
  }

  _getStoredCategory() {
    try {
      return this._normalizeCategory(localStorage.getItem('selectedCategory'));
    } catch (_) {
      return null;
    }
  }

  _setStoredCategory(category) {
    try {
      localStorage.setItem('selectedCategory', category);
    } catch (_) {}
  }

  _getRootPath() {
    const rootPath =
      (window.theme &&
        window.theme.routes &&
        typeof window.theme.routes.root === 'string' &&
        window.theme.routes.root) ||
      '/';

    if (rootPath === '/') return '/';
    return rootPath.replace(/\/+$/, '') || '/';
  }

  _isHomePath(pathname) {
    const rootPath = this._getRootPath();
    if (rootPath === '/') return pathname === '/';
    return pathname === rootPath || pathname === rootPath + '/';
  }

  _buildViewUrl(category) {
    const currentUrl = new URL(window.location.href);
    const targetUrl = new URL(window.location.origin);

    targetUrl.pathname = this._getRootPath();

    SAFE_HOME_QUERY_PARAMS.forEach((param) => {
      if (currentUrl.searchParams.has(param)) {
        targetUrl.searchParams.set(param, currentUrl.searchParams.get(param));
      }
    });

    targetUrl.searchParams.set('view', category);
    return targetUrl.toString();
  }

  _isEligibleHomeLink(link) {
    if (!link || !link.href || link.hasAttribute('download') || link.target === '_blank') {
      return false;
    }

    let linkUrl;

    try {
      linkUrl = new URL(link.href, window.location.origin);
    } catch (_) {
      return false;
    }

    if (linkUrl.origin !== window.location.origin || linkUrl.hash) return false;
    if (!this._isHomePath(linkUrl.pathname)) return false;

    const linkView = this._normalizeCategory(linkUrl.searchParams.get('view'));
    return link.search === '' || !!linkView || link.hasAttribute('data-home-link');
  }

  _syncHomeLinks(category) {
    const targetUrl = this._buildViewUrl(category);

    document.querySelectorAll('a[href]').forEach((link) => {
      if (!this._isEligibleHomeLink(link)) return;
      link.setAttribute('href', targetUrl);
    });
  }

  _bindHomeLinkDelegation() {
    if (this.homeLinkDelegationBound) return;
    this.homeLinkDelegationBound = true;

    document.addEventListener(
      'click',
      (event) => {
        const homeLink = event.target.closest('a[href]');
        if (!homeLink || !this._isEligibleHomeLink(homeLink)) return;
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const category = this.currentCategory || this._getStoredCategory() || DEFAULT_CATEGORY;
        const targetUrl = this._buildViewUrl(category);
        const currentHref = homeLink.href;

        if (currentHref === targetUrl) return;

        event.preventDefault();
        window.location.assign(targetUrl);
      },
      true
    );
  }

  updateButtonStates(category) {
    document
      .querySelectorAll(
        '.menu-btn, .fullscreen-category-nav__item, .drawer-menu-category-btn'
      )
      .forEach((btn) => {
        btn.classList.toggle('active', btn.getAttribute('data-category') === category);
      });
  }

  refreshSwipers() {
    const activeCategory = document.body.getAttribute('data-category');

    document.querySelectorAll('.swiper').forEach((el) => {
      if (!el.swiper) return;

      const categoryHost = el.closest('[data-category]');
      if (
        categoryHost &&
        activeCategory &&
        categoryHost.getAttribute('data-category') !== activeCategory
      ) {
        return;
      }

      el.swiper.update();
    });
  }

  dispatchCategoryChange(category, previousCategory) {
    document.dispatchEvent(
      new CustomEvent('categoryChanged', {
        detail: { category, previousCategory },
      })
    );
  }
}

window.categorySwitcher = new CategorySwitcher();

(function () {
  try {
    const target = sessionStorage.getItem('categorySwitchTarget');
    if (!target) return;

    const currentView = new URLSearchParams(window.location.search).get('view');
    if (currentView === target) sessionStorage.removeItem('categorySwitchTarget');
  } catch (_) {}
})();

let pendingCategoryChangedRaf = null;
document.addEventListener('categoryChanged', (event) => {
  if (pendingCategoryChangedRaf) {
    cancelAnimationFrame(pendingCategoryChangedRaf);
  }

  pendingCategoryChangedRaf = requestAnimationFrame(() => {
    pendingCategoryChangedRaf = null;

    const activeCategory =
      (event && event.detail && event.detail.category) ||
      document.body.getAttribute('data-category');

    setTimeout(() => {
      if (window.categorySwitcher) {
        window.categorySwitcher.refreshSwipers();
      }

      document
        .querySelectorAll('.featured-collection-grid .horizontal-scroll__wrapper')
        .forEach((el) => {
          const categoryHost = el.closest('[data-category]');
          if (
            categoryHost &&
            activeCategory &&
            categoryHost.getAttribute('data-category') !== activeCategory
          ) {
            return;
          }

          el.scrollLeft = 0;
        });
    }, 60);
  });
});

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    setTimeout(() => {
      if (window.categorySwitcher) {
        window.categorySwitcher.refreshSwipers();
        window.categorySwitcher.updateButtonStates(
          window.categorySwitcher.currentCategory || DEFAULT_CATEGORY
        );
      }
    }, 300);
  });
}
